import {
  AdminSpinner,
  Button,
  Disclaimer,
  Div,
  P,
  formatDateTimeFull,
  getMenuPublishHistory,
  lightTheme,
  renderRole,
  reportError,
} from '@vgrubs/components';
import { NoDataComponent } from '../../../adminComponents/NoDataComponent/NoDataComponent';
import { ModalPortal } from '../../../components/modal/modalPortal';
import { MenuPublishHistoryItemResponse } from 'components/src/types/api/menu.types';
import { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useParams } from 'react-router';
import { useSelectedMenu } from '../menuSelection';

const PAGE_LIMIT = 20;

export const MenuPublishHistory = ({
  isOpen,
  closeModal,
  onPreview,
}: {
  isOpen: boolean;
  closeModal: () => void;
  onPreview: (item: MenuPublishHistoryItemResponse) => void;
}) => {
  const { locationId, brandId } = useParams() as {
    locationId: string;
    brandId: string;
  };

  // History is per menu; the list reloads when the modal opens for another.
  const { menuId, menu: selectedMenu } = useSelectedMenu();

  const [items, setItems] = useState<MenuPublishHistoryItemResponse[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [ref, inView] = useInView();
  const requestedPageRef = useRef(0);

  const fetchPage = async (nextPage: number) => {
    if (requestedPageRef.current === nextPage) return;
    requestedPageRef.current = nextPage;

    try {
      setLoading(true);
      const { data } = await getMenuPublishHistory(
        locationId,
        brandId,
        { page: nextPage, limit: PAGE_LIMIT },
        menuId
      );

      setItems((prev) =>
        nextPage === 1 ? data.data : [...prev, ...data.data]
      );
      setHasMore(data.data.length >= PAGE_LIMIT);
      setPage(nextPage);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setItems([]);
      setHasMore(true);
      requestedPageRef.current = 0;
      fetchPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, locationId, brandId, menuId]);

  useEffect(() => {
    if (!isOpen) return;
    if (loading) return;
    if (!hasMore) return;
    if (!inView) return;
    fetchPage(page + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, hasMore, loading, isOpen]);

  const renderPublishedByName = (item: MenuPublishHistoryItemResponse) => {
    if (!item.publishedBy) return 'System';
    const first = item.publishedBy.firstName ?? '';
    const last = item.publishedBy.lastName ?? '';
    const name = `${first} ${last}`.trim();
    const role = renderRole(item.publishedBy.role);
    if (!name) return role || 'System';
    return role ? `${name} (${role})` : name;
  };

  return (
    <>
      {loading && <AdminSpinner />}
      <ModalPortal
        isOpen={isOpen}
        closeModal={closeModal}
        logo={false}
        header={
          selectedMenu
            ? `Publish History: ${selectedMenu.name}`
            : 'Publish History'
        }
        width="600px"
        textAlign="left"
      >
        {items.length > 0 &&
          items.map((item) => {
            const isRestored = !!item.restoredAt;
            return (
              <Div
                key={item.id}
                $box
                $p="16px 24px"
                $flex
                $alignCenter
                $between
                $gap="16px"
                $mb="8"
                $borderColor={isRestored ? lightTheme.primary : undefined}
                $columnSM
                $alignStartSM
              >
                <Div>
                  <Div $flex $column $gap="2px">
                    <P $m="0">
                      Published By: <b>{renderPublishedByName(item)}</b>
                    </P>
                    <Disclaimer $m="0">
                      Publish Date:{' '}
                      <b>{formatDateTimeFull(item.publishedAt)}</b>
                    </Disclaimer>
                  </Div>

                  {isRestored && (
                    <Div $flex $column $gap="2px" $mt="8">
                      <P $m="0">
                        Restored By: <b>{renderPublishedByName(item)}</b>
                      </P>
                      <Disclaimer $m="0">
                        Restored Date:{' '}
                        <b>{formatDateTimeFull(item.restoredAt ?? '')}</b>
                      </Disclaimer>
                    </Div>
                  )}
                </Div>

                <Button
                  $selected
                  $small
                  $w="170px"
                  $wSM="100%"
                  onClick={() => onPreview(item)}
                >
                  View {isRestored && 'Restored'}
                </Button>
              </Div>
            );
          })}

        {hasMore && <div ref={ref} />}

        {!loading && items.length === 0 && (
          <NoDataComponent
            height="200px"
            heightSM="200px"
            title="No Data"
            description="No publish history found"
          />
        )}
      </ModalPortal>
    </>
  );
};
